import { useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import { useForm } from "react-hook-form";
import ModalBasicLayout from "./ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import TextField from "../Form/TextField";
import { useTranslationApp } from "../../lib/i18next";
import {
  ANIMATE,
  BUTTON_CANCEL,
  FIELD_NUMBER_GREATER_THAN,
  MODAL_ANIMATE_IMAGE_PREVIEW_TITLE,
  MODAL_ANIMATE_IMAGE_PROMPT_HELPER,
  MODAL_ANIMATE_IMAGE_PROMPT_LABEL,
  MODAL_ANIMATE_IMAGE_PROMPT_PLACEHOLDER,
} from "../../i18n/keysTranslations";
import { ANIMATE_IMAGE_PROMPT_MAX_LENGTH } from "../../utils/animateImageConstants";

const ModalAnimateImagePreview = ({
  isOpen,
  onClose,
  imageUrl,
  onConfirm,
  isSubmitting = false,
}) => {
  const { t } = useTranslationApp();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      prompt: "",
    },
  });

  const promptValue = watch("prompt") || "";
  const isOverMaxLength =
    promptValue.trim().length > ANIMATE_IMAGE_PROMPT_MAX_LENGTH;

  useEffect(() => {
    if (isOpen) {
      reset({ prompt: "" });
    }
  }, [isOpen, imageUrl, reset]);

  const onSubmit = handleSubmit(({ prompt }) => {
    if (isSubmitting || isOverMaxLength) return;
    onConfirm((prompt || "").trim());
  });

  return (
    <ModalBasicLayout
      modalOpen={isOpen}
      onCloseModal={isSubmitting ? () => {} : onClose}
      title={t(MODAL_ANIMATE_IMAGE_PREVIEW_TITLE)}
      ariaLabel={t(MODAL_ANIMATE_IMAGE_PREVIEW_TITLE)}
      maxWidth="sm"
      fullWidth
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 1,
        }}
      >
        {imageUrl && (
          <Box
            sx={{
              mx: { xs: -3, sm: -5 },
              width: { xs: "calc(100% + 48px)", sm: "calc(100% + 80px)" },
              maxHeight: 320,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              bgcolor: "grey.100",
            }}
          >
            <Box
              component="img"
              src={imageUrl}
              alt=""
              sx={{
                maxWidth: "100%",
                maxHeight: 320,
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(MODAL_ANIMATE_IMAGE_PROMPT_LABEL)}
          </Typography>
          <TextField
            control={control}
            name="prompt"
            multiline
            minRows={3}
            maxRows={6}
            placeholder={t(MODAL_ANIMATE_IMAGE_PROMPT_PLACEHOLDER)}
            helperText={t(MODAL_ANIMATE_IMAGE_PROMPT_HELPER)}
            fullWidth
            variant="filled"
            errors={errors}
            formValues={{ prompt: promptValue }}
            showCounterCharacters
            maxCharacters={ANIMATE_IMAGE_PROMPT_MAX_LENGTH}
            disabled={isSubmitting}
            rules={{
              maxLength: {
                value: ANIMATE_IMAGE_PROMPT_MAX_LENGTH,
                message: t(FIELD_NUMBER_GREATER_THAN, {
                  value: ANIMATE_IMAGE_PROMPT_MAX_LENGTH,
                }),
              },
              required: false,
            }}
          />
        </Box>
      </Box>

      <DialogActions
        sx={{
          px: { xs: 3, sm: 5 },
          pb: 2,
          pt: 1,
          flexDirection: "column",
          gap: 1,
        }}
      >
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onClose,
            disabled: isSubmitting,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: ANIMATE,
            onClick: onSubmit,
            disabled: isSubmitting || isOverMaxLength,
            loading: isSubmitting,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalAnimateImagePreview;
